// Menu facts — the provider's side and the operator's side of WP3.
//
// A provider photographs their menu; `distill-provider-media` reads it into
// typed `provider_menus` rows; John approves or corrects them; only then do they
// reach the room (ruling 054/055 — facts feed selection and display, never
// narration). Nothing in this file can approve anything: `review_provider_menu`
// is superAdmin-gated inside the database.

import { supabase } from './supabase';

const FUNCTIONS_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

/** Business types for which "Add your menu" is offered. Pilot scope (decision 061). */
export const FOOD_TYPES = new Set(['restaurant', 'bar', 'food_drink']);

export function isFoodType(businessType: string | null | undefined): boolean {
  return !!businessType && FOOD_TYPES.has(businessType);
}

export interface MenuRow {
  id: string;
  provider_id: string;
  section: string | null;
  item: string;
  description: string | null;
  price_cents: number | null;
  price_cents_max: number | null;
  currency: string | null;
  dietary_flags: string[] | null;
  review_status: 'unreviewed' | 'approved' | 'rejected';
  source: string;
  source_photo_url: string | null;
  distilled_at: string | null;
}

export type DistillStatus = 'distilled' | 'refused' | 'already_read';

export interface DistillResponse {
  status: DistillStatus;
  rows: number;
  message: string;
}

/**
 * Ask the Edge Function to read an already-uploaded menu photo.
 *
 * `photo_url` must be on the media host — the function refuses anything else,
 * because otherwise a signed-in provider could have the server fetch and distil
 * any image on the web. Upload through `uploadMenuPhoto` and pass what it returns.
 *
 * A refusal is a normal 200, not an error: the model looked and honestly found
 * no menu. The message it carries is written for the provider — show it.
 */
export async function distillMenuPhoto(
  providerId: string,
  photoUrl: string,
): Promise<DistillResponse> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Your session expired — sign in again.');

  const resp = await fetch(`${FUNCTIONS_BASE}/distill-provider-media`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider_id: providerId, photo_url: photoUrl }),
  });
  const body = (await resp.json().catch(() => ({}))) as Partial<DistillResponse> & { error?: string };
  if (!resp.ok) throw new Error(body.error ?? `Could not read the menu (${resp.status}).`);
  return {
    status: (body.status ?? 'refused') as DistillStatus,
    rows: body.rows ?? 0,
    message: body.message ?? '',
  };
}

const MENU_COLUMNS =
  'id, provider_id, section, item, description, price_cents, price_cents_max, currency, ' +
  'dietary_flags, review_status, source, source_photo_url, distilled_at';

/** The provider's own rows. RLS scopes this to profiles they own. */
export async function listMyMenuRows(providerId: string): Promise<MenuRow[]> {
  const { data, error } = await supabase
    .from('provider_menus')
    .select(MENU_COLUMNS)
    .eq('provider_id', providerId)
    .order('section', { ascending: true, nullsFirst: true })
    .order('item', { ascending: true })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as unknown as MenuRow[];
}

export interface ReviewQueueRow extends MenuRow {
  business_name: string;
}

/**
 * The operator's review queue.
 *
 * Readable only because 20260905180000 added a superAdmin SELECT policy to
 * provider_menus — before it, an unreviewed row was invisible to everyone except
 * the provider who owned it, which is why there was no review queue to build.
 */
export async function listReviewQueue(status = 'unreviewed'): Promise<ReviewQueueRow[]> {
  const { data, error } = await supabase
    .from('provider_menus')
    .select(`${MENU_COLUMNS}, provider_profiles!inner(business_name)`)
    .eq('review_status', status)
    .order('distilled_at', { ascending: false })
    .limit(300);
  if (error) throw error;
  return (data ?? []).map((row) => {
    const { provider_profiles: profile, ...rest } = row as Record<string, unknown> & {
      provider_profiles?: { business_name?: string } | Array<{ business_name?: string }>;
    };
    const joined = Array.isArray(profile) ? profile[0] : profile;
    return { ...(rest as unknown as MenuRow), business_name: joined?.business_name ?? '(unknown)' };
  });
}

export interface ReviewResult {
  ok: boolean;
  id?: string;
  review_status?: string;
  reason?: string;
}

/**
 * Approve, reject, or correct a price.
 *
 * Editing is deliberately NOT approving: a corrected row stays `unreviewed` so
 * the operator still has to say yes to it explicitly.
 */
export async function reviewMenuRow(
  id: string,
  action: 'approve' | 'reject' | 'edit',
  price?: { cents?: number | null; centsMax?: number | null; currency?: string | null },
): Promise<ReviewResult> {
  const { data, error } = await supabase.rpc('review_provider_menu', {
    p_id: id,
    p_action: action,
    p_price_cents: price?.cents ?? null,
    p_price_cents_max: price?.centsMax ?? null,
    p_currency: price?.currency ?? null,
  });
  if (error) throw new Error(error.message);
  return data as ReviewResult;
}

/** "9,50" / "9.50" / "€9,50" → 950. Returns null when nothing sane can be read. */
export function euroTextToCents(raw: string): number | null {
  const cleaned = raw.replace(/[^\d.,]/g, '').replace(',', '.');
  if (!cleaned) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}

export function centsToEuroText(cents: number | null): string {
  return cents === null ? '' : (cents / 100).toFixed(2);
}
